import React from 'react';
import { OVERLAY_STATE, POSE_TARGETS } from './overlayConfig';
import { getDirection } from './getDirection';

const DIRECTION_TEXT = {
  UP: "Look UP",
  DOWN: "Look DOWN",
  LEFT: "Turn LEFT",
  RIGHT: "Turn RIGHT",
  "UP-LEFT": "Look UP-LEFT",
  "UP-RIGHT": "Look UP-RIGHT",
  "DOWN-LEFT": "Look DOWN-LEFT",
  "DOWN-RIGHT": "Look DOWN-RIGHT",
};

function DirectionalArrow({ dir, color = "#22c55e" }) {
  const pos = {
    UP:          { x: 200, y: 55,  rot: 0    },
    DOWN:        { x: 200, y: 445, rot: 180  },
    LEFT:        { x: 45,  y: 250, rot: -90  },
    RIGHT:       { x: 355, y: 250, rot: 90   },
    "UP-LEFT":   { x: 90,  y: 110, rot: -45  },
    "UP-RIGHT":  { x: 310, y: 110, rot: 45   },
    "DOWN-LEFT": { x: 90,  y: 390, rot: -135 },
    "DOWN-RIGHT":{ x: 310, y: 390, rot: 135  },
  }[dir];

  if (!pos) return null;

  return (
    <g transform={`translate(${pos.x} ${pos.y}) rotate(${pos.rot})`}>
      <path
        d="M -24 8 L 0 -16 L 24 8 L 14 8 L 0 -4 L -14 8 Z"
        fill={color}
        filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.6))"
      />
    </g>
  );
}

function AngleGauge({ pose, target }) {
  if (!target) return null;
  const cx = 200, cy = 475, r = 26;
  const norm = v => Math.max(-1, Math.min(1, v / 35));

  // In sign convention: target is [pitch, yaw]
  // In SVG: x is yaw (positive = left), y is -pitch (positive = up)
  const targetX = cx - norm(target[1]) * r;
  const targetY = cy - norm(target[0]) * r;
  const currentX = cx - norm(pose?.yaw || 0) * r;
  const currentY = cy - norm(pose?.pitch || 0) * r;

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} stroke="#334155" strokeWidth="1.5" fill="rgba(15, 23, 42, 0.8)" />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke="#475569" strokeWidth="1" />
      <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} stroke="#475569" strokeWidth="1" />
      {/* Target dot (amber) */}
      <circle cx={targetX} cy={targetY} r="4" fill="#f59e0b" />
      {/* Current dot (green) */}
      <circle cx={currentX} cy={currentY} r="5" fill="#22c55e" />
    </g>
  );
}

function HeadSilhouette({ angle, poseNumber }) {
  if (!angle) return null;
  const [pitch, yaw] = angle;
  // Tilt visually according to target pitch and yaw
  const transX = -yaw * 1.4;
  const transY = -pitch * 1.4;
  
  // For Pose B diagonal bands (pos 5-8), apply prominent 3D rotational perspective
  const isDiagonal = poseNumber >= 5 && poseNumber <= 8;
  const rotDeg = isDiagonal ? (-yaw * 0.6) : (-yaw * 0.35);
  const transform = `translate(${transX}, ${transY}) rotate(${rotDeg} 200 250)`;

  return (
    <g transform={transform}>
      <ellipse
        cx="200"
        cy="250"
        rx="105"
        ry="145"
        stroke="#f59e0b"
        strokeWidth="2.5"
        strokeDasharray="6 6"
        fill="rgba(245, 158, 11, 0.08)"
      />
      {/* Ghost nose marker pointing in diagonal orientation */}
      <circle cx="200" cy="250" r="4" fill="#f59e0b" />
      <line x1="165" y1="210" x2="185" y2="210" stroke="#f59e0b" strokeWidth="2" />
      <line x1="215" y1="210" x2="235" y2="210" stroke="#f59e0b" strokeWidth="2" />
      {/* Diagonal chin indicator line */}
      {isDiagonal && (
        <line x1="190" y1="380" x2="210" y2="380" stroke="#f59e0b" strokeWidth="2" strokeDasharray="3 3" />
      )}
    </g>
  );
}

export default function PoseOverlay({
  state = "SEARCHING",
  pose,
  target,
  poseNumber,
  holdProgress = 0,
  qualityWarning = null,
  activePromptText = null
}) {
  const cfg = OVERLAY_STATE[state] ?? OVERLAY_STATE.SEARCHING;
  const direction = getDirection(pose, target, poseNumber);
  const C = cfg.stroke;
  const targetTuple = POSE_TARGETS[poseNumber];

  let displayHint = cfg.hint;
  if (state === "TOO_FAR") {
    displayHint = "Come closer.";
  } else if (state === "SAME_BACKGROUND") {
    displayHint = "Same background detected. Please move.";
  } else if (state === "ALIGNING") {
    displayHint = qualityWarning || (direction ? DIRECTION_TEXT[direction] : (pose?.message || "Align your face in the oval"));
  }

  // Circular progress ring parameters
  const ringRadius = 12;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const strokeDashoffset = ringCircumference * (1 - (holdProgress || 0));

  return (
    <svg
      viewBox="0 0 400 500"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        userSelect: 'none'
      }}
    >
      <defs>
        <mask id="face-hole-mask">
          <rect width="400" height="500" fill="white" />
          <ellipse cx="200" cy="250" rx="105" ry="145" fill="black" />
        </mask>
      </defs>

      {/* Darkened vignette with cutout for the face */}
      <rect
        width="400"
        height="500"
        fill="rgba(0, 0, 0, 0.45)"
        mask="url(#face-hole-mask)"
      />

      {/* Rotating Target Ghost Head Silhouette for Pose Guidance */}
      {state === "ALIGNING" && targetTuple && (
        <HeadSilhouette angle={targetTuple} poseNumber={poseNumber} />
      )}

      {/* Main Face Oval Guide */}
      <ellipse
        cx="200"
        cy="250"
        rx="105"
        ry="145"
        fill="none"
        stroke={C}
        strokeWidth={state === "HOLDING" ? "4.5" : "3"}
        strokeDasharray={cfg.dash}
        style={{
          transition: "stroke 0.25s ease, stroke-width 0.25s ease",
          filter: state === "HOLDING" ? "drop-shadow(0px 0px 8px #22c55e)" : "none"
        }}
      />

      {/* Outside-the-oval Directional Guidance Arrow */}
      {direction && state === "ALIGNING" && (
        <DirectionalArrow dir={direction} color="#f59e0b" />
      )}

      {/* Angle gauge for directional poses */}
      {targetTuple && (
        <AngleGauge pose={pose} target={targetTuple} />
      )}

      {/* 600ms Green Hold Progress Ring */}
      {state === "HOLDING" && (
        <g transform="translate(200 420)">
          <circle cx="0" cy="0" r={ringRadius} stroke="#334155" strokeWidth="3" fill="rgba(15, 23, 42, 0.8)" />
          <circle
            cx="0"
            cy="0"
            r={ringRadius}
            stroke="#22c55e"
            strokeWidth="3.5"
            fill="none"
            strokeDasharray={ringCircumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform="rotate(-90)"
          />
        </g>
      )}

      {/* Dynamic Directional & Status Guidance Banner */}
      <g transform="translate(200 455)">
        <rect
          x="-175"
          y="-20"
          width="350"
          height="32"
          rx="16"
          fill="rgba(15, 23, 42, 0.88)"
          stroke={C}
          strokeWidth="1.2"
        />
        <text
          x="0"
          y="2"
          textAnchor="middle"
          fill="#f8fafc"
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {displayHint}
        </text>
      </g>

      {/* Active Diversity Instruction Prompt (Selfie variation advice) */}
      {activePromptText && (
        <g transform="translate(200 25)">
          <rect
            x="-185"
            y="-14"
            width="370"
            height="26"
            rx="13"
            fill="rgba(15, 23, 42, 0.85)"
            stroke="#38bdf8"
            strokeWidth="1"
          />
          <text
            x="0"
            y="4"
            textAnchor="middle"
            fill="#38bdf8"
            fontSize="11.5"
            fontWeight="600"
            fontFamily="system-ui, -apple-system, sans-serif"
          >
            {activePromptText}
          </text>
        </g>
      )}
    </svg>
  );
}
